<script setup lang="ts">
import { usePromptLibraryStore } from '@/stores/prompt_library';
import { NButton, type DataTableColumns } from 'naive-ui';
import { computed, h, onMounted, ref, VNode } from 'vue';
import { useRoute } from 'vue-router';
import { getFieldCategory, type FieldCategory } from '@/utils/fieldType';
import type { FieldType } from '@/types/evaluation';
import { logger } from '@/utils/logger';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import { gaiaContentWidth } from '@/components/design/themes/tokens';

// Store and router access
const store = usePromptLibraryStore();
const route = useRoute();

// Define types for row data and choices
interface PromptRow {
  name: string;
  prompt: string;
  response_type:
    | string
    | {
        name?: string;
        values?: { name: string; definition: string }[];
        multiple_choices?: boolean;
      };
  [key: string]: unknown;
}

// State
const tableError = ref(false);
const showChoicesModal = ref(false);
const selectedChoices = ref<{ name: string; definition: string }[]>([]);
const isMultipleChoice = ref(false);

// Get collection name from route params
const collectionName = computed((): string => {
  return route.params.collection.toString() || '';
});

// Collection title for display
const collectionTitle = computed((): string => {
  return decodeURIComponent(collectionName.value);
});

// Prompts in this collection
const prompts = computed((): PromptRow[] => {
  const collection = store.promptCollections[collectionTitle.value] ?? [];
  return collection;
});

// Is this collection currently loading?
const isLoading = computed((): boolean => {
  return !store.initialized || store.isCollectionLoading(collectionTitle.value);
});

// Table columns with error handling
const tableColumns = computed((): DataTableColumns<PromptRow> => {
  return [
    {
      title: 'Name',
      key: 'name',
      width: 200,
      fixed: 'left',
      ellipsis: {
        tooltip: true,
      },
      render(row: PromptRow): VNode {
        try {
          // Capitalize and format name: name_a => Name A
          const formattedName = row.name
            ? row.name
                .split('_')
                .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
            : 'Unnamed';
          return h('span', formattedName);
        } catch (error) {
          logger.error('Error rendering prompt name:', error);
          return h('span', { style: { color: 'red' } }, 'Error');
        }
      },
    },
    {
      title: 'Prompt',
      key: 'prompt',
      width: 400,
      render(row: PromptRow): VNode {
        try {
          // Return a div with white-space: pre-wrap to preserve line breaks
          return h(
            'div',
            {
              style: {
                whiteSpace: 'pre-wrap',
                maxHeight: '150px',
                overflow: 'auto',
              },
            },
            row.prompt || 'No prompt text'
          );
        } catch (error) {
          logger.error('Error rendering prompt text:', error);
          return h('span', { style: { color: 'red' } }, 'Error');
        }
      },
    },
    {
      title: 'Response Type',
      key: 'response_type',
      width: 160,
      render(row: PromptRow): VNode {
        try {
          // Check if response_type is a multiple choice format
          if (typeof row.response_type === 'object') {
            if ('values' in row.response_type && Array.isArray(row.response_type.values)) {
              // Create a button to show choices in a modal
              return h(
                NButton,
                {
                  text: true,
                  type: 'primary',
                  onClick: () => {
                    if (typeof row.response_type === 'object') {
                      selectedChoices.value = row.response_type.values ?? [];
                      isMultipleChoice.value = row.response_type.multiple_choices === true;
                    }
                    showChoicesModal.value = true;
                  },
                },
                {
                  default: () =>
                    typeof row.response_type === 'object' && row.response_type.multiple_choices
                      ? 'Multiple Choice'
                      : 'Choice',
                }
              );
            } else {
              return h('span', getResponseTypeLabel(row.response_type));
            }
          }
          return h('span', getResponseTypeLabel(row.response_type));
        } catch (error) {
          logger.error('Error rendering response type:', error);
          return h('span', { style: { color: 'red' } }, 'Error');
        }
      },
    },
  ];
});

// Function to return to prompt library
// Format response type for display
function getResponseTypeLabel(type: unknown): string {
  // Handle case when type is null or undefined
  if (type == null) {
    return 'Unknown';
  }

  // Handle case when type is an object with name property
  if (typeof type === 'object' && 'name' in type) {
    return (type as { name: string }).name;
  }

  // Use centralized field type utility for categorization
  const category = getFieldCategory(type as FieldType);

  // Map categories to user-friendly labels
  const categoryLabels: Record<FieldCategory, string> = {
    boolean: 'Yes/No',
    number: 'Number',
    datetime: 'Date & Time',
    currency: 'Currency Amount',
    'enum-single': 'Choice',
    'enum-multiple': 'Multiple Choice',
    list: 'List',
    entity: 'Entity',
    'calendar-event': 'Calendar Schedule',
    other: 'Text',
  };

  return categoryLabels[category] || 'Text';
}

// Initialize
onMounted(async () => {
  try {
    if (!store.initialized) {
      await store.initialize();
    }

    // Load collection if not already loaded
    if (collectionTitle.value && (!prompts.value.length || prompts.value.length === 0)) {
      await store.getPromptCollection(collectionTitle.value);
    }
  } catch (error) {
    logger.error('Error loading prompt details:', error);
    tableError.value = true;
  }
});
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.repository }}</n-breadcrumb-item>
      <n-breadcrumb-item clickable>
        <router-link to="/prompt-library">{{ navLabels.gaiaPrompts }}</router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item>{{ collectionTitle }}</n-breadcrumb-item>
    </template>

    <n-space vertical>
      <!-- Header -->
      <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
        {{ collectionTitle }}
      </gaia-header>

      <!-- Loading state -->
      <n-card v-if="isLoading">
        <n-skeleton text :repeat="5" />
      </n-card>

      <!-- Prompts table -->
      <n-card v-else-if="prompts.length > 0">
        <n-alert
          v-if="tableError"
          type="error"
          title="Error displaying data"
          style="margin-bottom: 16px"
        >
          There was an error displaying some prompt data. This might be due to unexpected data
          formats.
        </n-alert>

        <div>
          <n-data-table
            :columns="tableColumns"
            :data="prompts"
            :bordered="true"
            :pagination="false"
            :max-height="500"
            :min-height="200"
            :row-key="(row: PromptRow) => row.name + ((row.index as string) || '')"
            :scroll-x="800"
          />
        </div>
      </n-card>

      <!-- Empty state -->
      <n-empty v-else description="No prompts found in this collection" />

      <!-- Choices Modal -->
      <n-modal
        v-model:show="showChoicesModal"
        preset="card"
        title="Available Choices"
        style="max-width: 600px"
      >
        <n-space vertical>
          <n-alert type="info" style="margin-bottom: 16px">
            <template #header>
              {{ isMultipleChoice ? 'Multiple Choice' : 'Single Choice' }} Response
            </template>
            {{
              isMultipleChoice
                ? 'AI can select multiple options from the list.'
                : 'AI must select exactly one option from the list.'
            }}
          </n-alert>

          <n-list bordered>
            <n-list-item v-for="(choice, index) in selectedChoices" :key="index">
              <n-space vertical align="start">
                <n-text strong>{{ choice.name }}</n-text>
                <n-text depth="2">{{ choice.definition }}</n-text>
              </n-space>
            </n-list-item>
          </n-list>

          <div style="margin-top: 20px; text-align: right">
            <n-button @click="showChoicesModal = false">Close</n-button>
          </div>
        </n-space>
      </n-modal>
    </n-space>
  </Layout>
</template>
